/**
 * `contact_support`'s card: the short note the agent wrote for CaseBlink
 * Support, read-only, with Not Now and Open Support Chat. Only the button opens
 * the Messenger, on a new message prefilled with the note; the person edits it
 * there and presses Send. Nothing from the thread goes with it.
 */
import type { DynamicToolUIPart, ToolUIPart } from "ai";
import { CheckCircle2, CircleQuestionMark, MessageCircle } from "lucide-react";
import { useState, type FC } from "react";

import { Button } from "@/components/ui/button";
import { IconTile } from "@/components/ui/icon-tile";
import type { SupportCardState } from "@/features/agents/useAgentChat";
import { openSupport } from "@/helpers/helpers";
import { cn } from "@/lib/utils";

import { useSupportCardsContext } from "../support/SupportCardsContext";

type Props = {
  part: ToolUIPart | DynamicToolUIPart;
  className?: string;
};

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null && !Array.isArray(v);

/** The note the call returned (`summary`, or its `open.summary`). */
export const supportNoteOf = (output: unknown): string | null => {
  if (!isRecord(output)) return null;
  const open = isRecord(output.open) ? output.open : null;
  const note =
    typeof output.summary === "string"
      ? output.summary
      : typeof open?.summary === "string"
      ? open.summary
      : "";
  return note.trim() || null;
};

export const SupportHandoffCard: FC<Props> = ({ part, className }) => {
  const cards = useSupportCardsContext();
  const [localState, setLocalState] = useState<SupportCardState | null>(null);
  const note =
    part.state === "output-available" ? supportNoteOf(part.output) : null;
  if (!note) return null;

  const state = cards?.stateOf(part.toolCallId) ?? localState;
  const settle = (next: SupportCardState) => {
    setLocalState(next);
    cards?.setState(part.toolCallId, next);
  };
  const open = () => {
    openSupport(note);
    settle("opened");
  };

  if (state) {
    const opened = state === "opened";
    return (
      <div
        className={cn(
          "flex items-center gap-2 rounded-xl px-3 py-2 ring-1 ring-line-2",
          className
        )}
        data-testid="support-card"
        data-state={state}
      >
        {opened ? (
          <CheckCircle2 className="size-3.5 shrink-0 text-success" />
        ) : (
          <MessageCircle className="size-3.5 shrink-0 text-fg-3" />
        )}
        <span
          className={cn(
            "min-w-0 flex-1 text-base",
            opened ? "text-fg" : "text-fg-2"
          )}
        >
          {opened
            ? "Support chat opened with your message"
            : "Talk to CaseBlink Support · Not now"}
        </span>
        {opened ? (
          <Button variant="ghost" size="xs" onClick={open}>
            Open Again
          </Button>
        ) : null}
      </div>
    );
  }

  return (
    <div
      className={cn(
        "flex flex-col gap-3 rounded-2xl bg-elevated p-3 ring-1 ring-line-2",
        className
      )}
      data-testid="support-card"
      data-state="pending"
    >
      <div className="flex items-start gap-2.5">
        <IconTile icon={CircleQuestionMark} />
        <div className="flex min-w-0 flex-col gap-0.5">
          <span className="text-base font-medium text-fg">
            Talk to CaseBlink Support
          </span>
          <span className="text-sm text-fg-3">
            A person on the CaseBlink team replies in the support chat, usually
            within a few hours.
          </span>
        </div>
      </div>
      <div className="flex flex-col gap-1 rounded-lg bg-fill-1 px-2.5 py-2">
        <p
          className="text-base whitespace-pre-wrap text-fg-2"
          data-testid="support-card-note"
        >
          {note}
        </p>
        <span className="text-sm text-fg-3">
          Only this note goes to support, and you can edit it before you send.
        </span>
      </div>
      <div className="flex justify-end gap-2">
        <Button onClick={() => settle("dismissed")}>Not Now</Button>
        <Button variant="primary" onClick={open}>
          <MessageCircle />
          Open Support Chat
        </Button>
      </div>
    </div>
  );
};
